import React, { useCallback, useEffect, useState } from 'react';
import {
  HiPlay, HiPause, HiCog, HiPlus, HiRefresh,
} from 'react-icons/hi';
import apiClient from '../../api/client';
import { useNotification } from '../../context/NotificationContext';
import ModalOverlay from '../../components/ModalOverlay';
import MpDailyChart, { KpiGrid, mpFmt as fmt } from '../../components/MpDailyChart';
import MarketplaceStrategyModal from '../../components/MarketplaceStrategyModal';
import { formatMarketplaceStrategy, strategyFormToPayload } from '../../utils/marketplaceStrategies';

export default function AvitoAdsCabinet({ clientId }) {
  const { notify } = useNotification();
  const [adsCampaigns, setAdsCampaigns] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editAds, setEditAds] = useState(null);
  const [showCreateAds, setShowCreateAds] = useState(false);
  const [newAdsName, setNewAdsName] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [manageRes, summaryRes] = await Promise.all([
        apiClient.get('/avito/ads/campaigns/manage', { params: { client_id: clientId } }),
        apiClient.get('/avito/ads/campaigns/summary', { params: { client_id: clientId } }),
      ]);
      setAdsCampaigns(manageRes.data?.items || []);
      setSummary(summaryRes.data);
    } catch (e) {
      notify(e.response?.data?.message || 'Ошибка загрузки', 'error');
    } finally {
      setLoading(false);
    }
  }, [clientId, notify]);

  useEffect(() => { load(); }, [load]);

  const saveAds = async (values) => {
    try {
      const payload = strategyFormToPayload(values, 'avito_ads');
      await apiClient.patch(`/avito/ads/campaigns/${editAds.campaign_id}`, {
        client_id: clientId,
        ...payload,
        bidder_enabled: values.bidder_enabled ? 1 : 0,
        automation_enabled: values.automation_enabled ? 1 : 0,
      });
      notify('Сохранено', 'success');
      load();
    } catch (e) {
      notify(e.response?.data?.message || 'Ошибка сохранения', 'error');
      throw e;
    }
  };

  const toggleAds = async (c, on) => {
    const url = on
      ? `/avito/ads/campaigns/${c.campaign_id}/activate`
      : `/avito/ads/campaigns/${c.campaign_id}/deactivate`;
    await apiClient.post(url, { client_id: clientId });
    load();
  };

  const createAds = async () => {
    await apiClient.post('/avito/ads/campaigns', { client_id: clientId, name: newAdsName });
    setShowCreateAds(false);
    setNewAdsName('');
    load();
  };

  if (loading) return <p className="text-[var(--wb-text-muted)]">Загрузка Avito Реклама…</p>;

  const totals = summary?.totals || {};

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Avito Реклама</h2>
          <p className="text-sm text-[var(--wb-text-muted)]">
            Медийная реклама Avito — кампании, CPA/ROI по целям Яндекс.Метрики
          </p>
        </div>
        <div className="flex gap-2">
          <button type="button" className="wb-btn-secondary" onClick={load}>
            <HiRefresh className="w-4 h-4" />
          </button>
          <button type="button" className="wb-btn text-sm" onClick={() => setShowCreateAds(true)}>
            <HiPlus className="w-4 h-4" /> Кампания
          </button>
        </div>
      </div>

      <KpiGrid items={[
        { label: 'Расход', value: `${fmt(totals.spend)} ₽` },
        { label: 'Показы', value: fmt(totals.views) },
        { label: 'Клики', value: fmt(totals.clicks) },
        { label: 'CTR', value: `${fmt(totals.ctr, 1)}%` },
        { label: 'CPC', value: totals.cpc != null ? `${fmt(totals.cpc, 2)} ₽` : '—' },
        { label: 'Цели (Метрика)', value: fmt(totals.goal_completions) },
        { label: 'Ценность целей', value: `${fmt(totals.goal_value)} ₽` },
        { label: 'CPA', value: totals.cpa != null ? `${fmt(totals.cpa)} ₽` : '—' },
        { label: 'ROI', value: totals.roi != null ? `${fmt(totals.roi, 1)}%` : '—' },
      ]} />

      <MpDailyChart
        data={summary?.daily || []}
        lines={[
          { key: 'spend', name: 'Расход, ₽', color: '#3b82f6' },
          { key: 'views', name: 'Показы', color: '#4f46e5' },
          { key: 'clicks', name: 'Клики', color: '#10b981' },
          { key: 'goal_completions', name: 'Цели', color: '#f97316' },
          { key: 'goal_value', name: 'Ценность, ₽', color: '#8b5cf6' },
          { key: 'ctr', name: 'CTR, %', color: '#14b8a6', yAxisId: 'right' },
          { key: 'roi', name: 'ROI, %', color: '#ec4899', yAxisId: 'right' },
        ]}
      />

      <div className="wb-card overflow-x-auto">
        <table className="wb-data-table">
          <thead>
            <tr>
              <th>Кампания</th>
              <th>Статус</th>
              <th className="wb-col-num">Показы</th>
              <th className="wb-col-num">Клики</th>
              <th className="wb-col-num">CTR</th>
              <th className="wb-col-num">CPC</th>
              <th className="wb-col-num">Расход</th>
              <th className="wb-col-num">Цели</th>
              <th className="wb-col-num">Ценность</th>
              <th className="wb-col-num">CPA</th>
              <th className="wb-col-num">ROI</th>
              <th>Стратегия</th>
              <th className="wb-col-actions" aria-label="Действия" />
            </tr>
          </thead>
          <tbody>
            {adsCampaigns.map((c) => (
              <tr key={c.campaign_id}>
                <td className="font-medium">{c.name}</td>
                <td>{c.is_running ? 'Активна' : 'Пауза'}</td>
                <td className="wb-col-num">{fmt(c.views)}</td>
                <td className="wb-col-num">{fmt(c.clicks)}</td>
                <td className="wb-col-num">{fmt(c.ctr, 1)}%</td>
                <td className="wb-col-num">{c.cpc != null ? `${fmt(c.cpc, 2)} ₽` : '—'}</td>
                <td className="wb-col-num">{fmt(c.spend)} ₽</td>
                <td className="wb-col-num">{fmt(c.goal_completions)}</td>
                <td className="wb-col-num">{fmt(c.goal_value)} ₽</td>
                <td className="wb-col-num">{c.cpa != null ? `${fmt(c.cpa)} ₽` : '—'}</td>
                <td className="wb-col-num">{c.roi != null ? `${fmt(c.roi, 1)}%` : '—'}</td>
                <td>
                  <div className="text-xs text-gray-600 max-w-[140px]">{formatMarketplaceStrategy(c, 'avito_ads')}</div>
                </td>
                <td className="wb-col-actions">
                  <div className="inline-flex items-center justify-center gap-1">
                    {c.is_running ? (
                      <button type="button" onClick={() => toggleAds(c, false)} aria-label="Пауза">
                        <HiPause className="w-4 h-4" />
                      </button>
                    ) : (
                      <button type="button" onClick={() => toggleAds(c, true)} aria-label="Запуск">
                        <HiPlay className="w-4 h-4" />
                      </button>
                    )}
                    <button type="button" onClick={() => setEditAds({ ...c })} aria-label="Настройки">
                      <HiCog className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <MarketplaceStrategyModal
        open={Boolean(editAds)}
        onClose={() => setEditAds(null)}
        title={editAds?.name}
        subtitle={`ID ${editAds?.campaign_id} · ${editAds?.price_model || 'cpc'}`}
        marketplace="avito_ads"
        initialRow={editAds}
        onSave={saveAds}
      />

      {showCreateAds && (
        <ModalOverlay onClose={() => setShowCreateAds(false)}>
          <div className="wb-card p-6 max-w-md w-full space-y-3" onClick={(e) => e.stopPropagation()}>
            <input className="wb-input w-full" placeholder="Название кампании" value={newAdsName}
              onChange={(e) => setNewAdsName(e.target.value)} />
            <button type="button" className="wb-btn" onClick={createAds} disabled={!newAdsName.trim()}>Создать</button>
          </div>
        </ModalOverlay>
      )}
    </div>
  );
}
